/* eslint-disable camelcase */
import axios from 'axios';
import _ from 'lodash';
import { delay } from 'redux-saga';
import message from '../components/Message';

const baseURL = '';
const timeout = 30000;

// A fix for window.location.origin in Internet Explorer
let origin = process.env.ENVIRONMENT === 'development' ? 'http://127.0.0.1:3000' : window.location.origin;
if (!origin) {
  // eslint-disable-next-line prefer-template
  origin = window.location.protocol + '//' + window.location.hostname + (window.location.port ? `:${window.location.port}` : '');
}
const showError = _.debounce(async (errors, invalid = false) => {
  if (_.isArray(errors)) {
    message.error(errors[0].message);
  }
  if (_.isString(errors)) {
    message.error(errors);
  }
  if (invalid) {
    await delay(2500);
    window.location.href = `${origin}/sign_in`;
  }
}, 2500, { leading: true, trailing: false });

class Ajax {
  constructor() {
    this.ajax = axios.create({
      baseURL,
      timeout,
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
    this.ajax.interceptors.response.use(res => res, ((error) => {
      if (error.message === `timeout of ${timeout}ms exceeded`) {
        // message.error(i18n.get('global.timeout'));
      }
      if (error.response) {
        const { response: { data, status } } = error;
        const { error: errors } = data;
        if (errors) {
          // todo 目前401报错，error是string而不是数组
          showError(errors, status === 401);
        }
        data.status = status;
        return Promise.reject(data);
      }
      return Promise.reject(error);
    }));
  }

  /* 与redux无关的api begin */

  // 获取链接信息
  getLinkInfo = url => this.ajax.get('/external_pages/info', {
    params: {
      url,
    },
  });

  // 获取视频信息
  getVideoInfo = data => this.ajax.post('/videos.json', data);

  // 获取音频信息
  getAudioInfo = note_id => this.ajax.get(`/author/notes/${note_id}/audio/post_policy`);

  // 身份验证时 请求发送验证码
  sendCaptcha = data => this.ajax.post('/secure_sessions/send_code', data);

  // 身份验证时 验证验证码
  verifyCaptcha = data => this.ajax.post('/secure_sessions/activate', data);

  // 获取国家手机区号
  getCountyOtpInfo = data => this.ajax.get('/countries');

  // 绑定手机请求发送 验证码
  sendCaptchaWhenBindPhone = data => this.ajax.post('/mobile_phone/send_code', data);

  // 绑定手机请求验证 验证码
  bindPhone = data => this.ajax.put('/mobile_phone/bind', data);

  // 搜索专题的接口
  searchCollections = (note_id, queryword) => {
    return this.ajax.get(`/notes/${note_id}/submit_collections/search`, {
      params: {
        q: queryword,
      },
    });
  };
  // 获取用户管理的专题
  getEditableCollections = (note_id, page, per_page) => {
    return this.ajax.get(`/notes/${note_id}/editable_collections`, {
      params: {
        page,
        per_page,
      },
    });
  };

  // 获取用户最近投稿专题，分页有点奇怪
  getLatestCollections = (note_id, page, per_page, rest = {}) => this.ajax.get(
    `/notes/${note_id}/submitted_collections`,
    {
      params: {
        count: per_page,
        max_id: rest.maxId,
      },
    },
  );
  // 获取推荐的专题
  getRecCollections = (note_id, page, per_page) => this.ajax.get(
    `/notes/${note_id}/recommended_collections`,
    {
      params: {
        page,
        per_page,
      },
    },
  );

  // 投稿接口
  contributeToCollection = (collection_id, note_id) => this.ajax.post(`/notes/${note_id}/submit`, {
    collection_id,
  });

  /* 与redux无关的api end */

  // 获取验证渠道信息
  getOtpChannel = data => this.ajax.get('/otp_channels');

  // 获取用户信息
  getUser = () => this.ajax.get('/author/current_user');

  // 切换同步滚动
  switchScroll = on => this.ajax.post('/author/current_user/set_md_auto_scroll', { on: +on });

  // 获取文集列表
  getNotebooks = () => this.ajax.get('/author/notebooks');

  // 排序文集
  sortNotebooks = sequence => this.ajax.put('/author/notebooks/sequence', {
    sequence,
  });
  // 修改文集名称
  updateBookName = (nbId, name) => this.ajax.put(`/author/notebooks/${nbId}`, {
    name,
  });

  // 获取文章列表
  getNotes = (nbId, page = 1, count = 10, nid = null) => this.ajax.get(`/author/notebooks/${nbId}/notes`, {
    params: {
      page,
      count,
      ...(nid && { nid })
    }
  });

  // 排序文章
  sortNotes = sequence => this.ajax.put('/author/notes/sequence', {
    sequence,
  });

  // 获取文章内容
  getNoteContent = noteId => this.ajax.get(`/author/notes/${noteId}/content`);

  // 创建文集
  newNoteBook = name => this.ajax.post('/author/notebooks', {
    name,
  });

  // 删除文集
  deleteNoteBookSoft = nbId => this.ajax.post(`/author/notebooks/${nbId}/soft_destroy`);

  // 创建文章
  newNote = data => this.ajax.post('/author/notes', {
    notebook_id: data.nbId,
    title: data.title,
    at_bottom: data.atBottom,
  });

  // 发布文章
  publishNote = (noteId, forcedPublish) => {
    return this.ajax.post(`/author/notes/${noteId}/publicize`, forcedPublish ? {
      // 强制发布
      schedule_publish_at: -1,
    } : {});
  };

  // 更新文章内容（包含标题）
  updateNote = (noteId, autosaveControl, title, content) => this.ajax.put(`/author/notes/${noteId}`, {
    id: noteId,
    autosave_control: autosaveControl, // eslint-disable-line camelcase
    title,
    content,
  });

  // 设置禁止转载
  setReprintAble = noteId => this.ajax.put(`/author/notes/${noteId}/toggle_reprintable`);

  // 私密文章
  privateNote = noteId => this.ajax.post(`/author/notes/${noteId}/privatize`);

  // 移动文章
  moveNote = (newNbId, noteId) => this.ajax.post(`/author/notes/${noteId}/move`, { notebook_id: newNbId });

  // 恢复文章
  recoveryNote = noteId => this.ajax.post(`/author/recycle/notes/${noteId}/restore`);

  // 将文章移入回收站
  deleteNoteSoft = noteId => this.ajax.post(`/author/notes/${noteId}/soft_destroy`);

  // 彻底删除文章
  deleteNote = noteId => this.ajax.delete(`/author/recycle/notes/${noteId}`);

  // 获取历史版本的列表
  getNoteHistoryList = noteId => this.ajax.get(`/author/notes/${noteId}/note_logs`);

  // 获取某一个历史版本的内容  数据迁移需要临时加个note_id
  getNoteHistoryContent = (historyId, note_id) => this.ajax.get(`/author/note_logs/${historyId}`, {
    params: {
      note_id,
    },
  });

  // 恢复历史版本 数据迁移需要临时加个note_id
  restoreHistoryContent = (historyId, note_id) => this.ajax.post(`/author/note_logs/${historyId}/restore`, {
    note_id,
  });

  // 获取回收站文章列表
  getTrashNotesList = () => this.ajax.get('/author/recycle/notes');

  // 切换白天与黑夜模式
  changeReadMode = readMode => this.ajax.put('/settings/view_mode', {
    read_mode: readMode,
  });

  changeFontType = fontType => this.ajax.put('/settings/view_mode', {
    default_font: fontType,
  });

  changeLocale = locale => this.ajax.put('/settings/view_mode', {
    locale,
  });

  changeEditorType = editorType => this.ajax.put('/author/settings', {
    preferred_note_type: editorType,
  });

  // 同意付费文章协议
  agreeThePNAgreement = () => this.ajax.post('/paid_note_agreements/agree');

  // 获取付费文章内容
  getPayNoteContent = note_id => this.ajax.get(`/author/paid_notes/notes/${note_id}/paid_setting`);

  // 保存付费文章设置项
  updatePNSetting = data => this.ajax.post(`/author/paid_notes/notes/${data.note_id}/publicize`, data);

  // 获取付费文章协议内容
  getPayNoteAgreement = () => this.ajax.get('/paid_note_agreement.html');

  // 上传音频标题与作者
  putAudioMessage = (note_id, params) => this.ajax.put(`/author/notes/${note_id}/audio`, params);

  // 删除音频
  deleteAudio = note_id => this.ajax.delete(`/author/notes/${note_id}/audio`);

  // 更新音频URL
  getAudioUrl = note_id => this.ajax.get(`/author/notes/${note_id}/audio/play_url`);

  // 获取自定义封面
  getCoverImg = note_id => this.ajax.get(`/author/notes/${note_id}/cover_images`);

  // 上传自定义封面
  postCoverImg = ({ noteId, flowType, abbr, coverImgUrls }) => this.ajax.post(`/author/notes/${noteId}/update_cover_images`, {
    flow_type: flowType,
    abbr,
    cover_image_urls: coverImgUrls,
  });

  // 获取文章信息
  getNoteMessage = noteId => this.ajax.get(`/author/notes/${noteId}`);

  // 置顶文章
  postSetNoteTop = noteId => this.ajax.post(`/author/notes/${noteId}/set_top`);

  // 取消置顶文章
  postUnsetNoteTop = noteId => this.ajax.post(`/author/notes/${noteId}/unset_top`);

  // 获取定时发文剩余次数
  getTimePublishCount = () => this.ajax.get('/user_function_cards/timing_publish/quantity');

  // 获取定时发文的详细信息
  getTimePublishCardInfo = () => this.ajax.get('/function_cards/timing_publish');

  // 设置定时发文时间
  setNotePublishTime = (noteId, time) => this.ajax.post(`/author/notes/${noteId}/publish_schedule/set_time`, {
    schedule_time: time,
  });

  // 获取定时发文guid
  getTimePublishGuid = () => this.ajax.post('/function_cards/timing_publish/buy', {
    quantity: 1,
  });

  // 通过guid进行下单
  payOrder = (guid, channel) => this.ajax.post(`/orders/${guid}/pay`, { channel });

  // 查询订单状态
  getOrderStatus = guid => this.ajax.get(`/orders/${guid}/state`);

  // 获取显示认证test
  getABtest = () => this.ajax.get('/shakespeare/ab_settings')

  // 获取认证跳转
  getAuthorInfo = () => this.ajax.get('/asimov/real_name_auth/auth_info')
}

export default new Ajax();



// WEBPACK FOOTER //
// ./src/utils/ajax.js